import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import {
  getPipelines,
  getProjectStatuses,
  getCompanies,
  getSourceFields,
  createProjectStageConfig,
  updateProjectStageConfig,
  deleteProjectStageConfig,
  parseApiError,
} from '../actions';
import type {
  ProjectStageConfig,
  ProjectStatus,
  CompanyOption,
  Pipeline,
  PipelineStage,
  SourceField,
} from '../types';
import { FieldMappingEditor } from '../FieldMappingEditor';
import type { KeyedFieldMapping } from '../FieldMappingEditor/FieldMappingEditor';
import { StageMappingEditor } from '../StageMappingEditor';
import type { StageMappingRow } from '../StageMappingEditor/StageMappingEditor';

import classes from './jobConfigForm.module.css';

interface JobConfigFormProps {
  locationId: string;
  ssoToken: string;
  pipelineConfigs: ProjectStageConfig[];
  templateConfig?: ProjectStageConfig | null;
  lockedPipeline?: Pipeline;
  onSave: () => void;
  onCancel: () => void;
}

function JobConfigForm({ locationId, ssoToken, pipelineConfigs, templateConfig, lockedPipeline, onSave, onCancel }: JobConfigFormProps) {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [statuses, setStatuses] = useState<ProjectStatus[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [sourceFields, setSourceFields] = useState<SourceField[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);

  const [selectedPipeline, setSelectedPipeline] = useState<string>('');
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedStatusId, setSelectedStatusId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [syncContactNotes, setSyncContactNotes] = useState<boolean>(false);
  const [fieldMappings, setFieldMappings] = useState<KeyedFieldMapping[]>([]);
  const [stageMappings, setStageMappings] = useState<StageMappingRow[]>([]);
  const nextRowKey = useRef(1);
  const nextStageMappingKey = useRef(1);

  // Track the original additional config IDs so we can detect removals
  const originalAdditionalConfigIds = useRef<number[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive primary config (first in the array) and additional configs
  const primaryConfig = pipelineConfigs.length > 0 ? pipelineConfigs[0] : null;
  const editingConfig = primaryConfig;

  // Load dropdown data
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [pipelinesData, statusesData, companiesData, sourceFieldsData] = await Promise.all([
          getPipelines(locationId, ssoToken),
          getProjectStatuses(ssoToken),
          getCompanies(ssoToken),
          getSourceFields(ssoToken),
        ]);

        if (!mounted) return;

        setPipelines(pipelinesData);
        setStatuses(statusesData);
        setCompanies(companiesData);
        setSourceFields(sourceFieldsData);

        // Pre-select locked pipeline
        if (lockedPipeline) {
          setSelectedPipeline(lockedPipeline.id);
          setStages(lockedPipeline.stages);
        }

        // Pre-populate if editing
        if (primaryConfig) {
          setSelectedPipeline(primaryConfig.pipeline_id);
          setSelectedStage(primaryConfig.trigger_stage_id);
          setSelectedStatusId(String(primaryConfig.project_status_id));
          setSelectedCompanyId(primaryConfig.company_id ? String(primaryConfig.company_id) : (companiesData[0] ? String(companiesData[0].id) : ''));
          setIsActive(primaryConfig.is_active);
          setSyncContactNotes(primaryConfig.sync_contact_notes ?? false);

          let keyCounter = 1;
          setFieldMappings(
            (primaryConfig.field_mappings || []).map((m) => ({
              rowKey: keyCounter++, // eslint-disable-line no-plusplus
              source_field_key: m.source_field_key,
              target_field: m.target_field,
            }))
          );
          nextRowKey.current = keyCounter;

          // Set stages from matching pipeline
          const matchingPipeline = pipelinesData.find((p) => p.id === primaryConfig.pipeline_id);
          if (matchingPipeline) {
            setStages(matchingPipeline.stages);
          }

          // Populate stage mappings from additional configs
          const additionalConfigs = pipelineConfigs.slice(1);
          let stageMappingKeyCounter = 1;
          const initialStageMappings: StageMappingRow[] = additionalConfigs.map((c) => ({
            rowKey: stageMappingKeyCounter++, // eslint-disable-line no-plusplus
            stageId: c.trigger_stage_id,
            statusId: String(c.project_status_id),
            configId: c.id,
          }));
          setStageMappings(initialStageMappings);
          nextStageMappingKey.current = stageMappingKeyCounter;
          originalAdditionalConfigIds.current = additionalConfigs.map((c) => c.id);
        } else if (templateConfig) {
          // Pre-fill defaults from an existing config (template)
          setSelectedStatusId(String(templateConfig.project_status_id));
          setSelectedCompanyId(templateConfig.company_id ? String(templateConfig.company_id) : (companiesData[0] ? String(companiesData[0].id) : ''));
          setIsActive(templateConfig.is_active);
          setSyncContactNotes(templateConfig.sync_contact_notes ?? false);

          let keyCounter = 1;
          setFieldMappings(
            (templateConfig.field_mappings || []).map((m) => ({
              rowKey: keyCounter++, // eslint-disable-line no-plusplus
              source_field_key: m.source_field_key,
              target_field: m.target_field,
            }))
          );
          nextRowKey.current = keyCounter;
        } else if (companiesData[0]) {
          setSelectedCompanyId(String(companiesData[0].id));
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load configuration data');
          // eslint-disable-next-line no-console
          console.error('Error fetching job config data:', err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [ssoToken, primaryConfig?.id]);

  const handleAddMapping = useCallback(() => {
    const key = nextRowKey.current;
    nextRowKey.current += 1;
    setFieldMappings((prev) => [...prev, { rowKey: key, source_field_key: '', target_field: '' }]);
  }, []);

  const handleAddStageMapping = useCallback(() => {
    const key = nextStageMappingKey.current;
    nextStageMappingKey.current += 1;
    setStageMappings((prev) => [...prev, { rowKey: key, stageId: '', statusId: '' }]);
  }, []);

  const handlePipelineChange = useCallback(
    (pipelineId: string) => {
      setSelectedPipeline(pipelineId);
      setSelectedStage('');
      setStageMappings([]);
      const pipeline = pipelines.find((p) => p.id === pipelineId);
      setStages(pipeline?.stages || []);
    },
    [pipelines]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!selectedPipeline || !selectedStage || !selectedStatusId) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate field mappings - each row must be complete
      const hasIncompleteMapping = fieldMappings.some(
        (m) => !m.source_field_key || !m.target_field
      );
      if (hasIncompleteMapping) {
        setError('Please complete all field mappings or remove incomplete ones');
        return;
      }

      // Validate stage mappings - each row must be complete
      const hasIncompleteStageMapping = stageMappings.some(
        (m) => !m.stageId || !m.statusId
      );
      if (hasIncompleteStageMapping) {
        setError('Please complete all stage mappings or remove incomplete ones');
        return;
      }

      // Strip rowKey before sending to API
      const payload = {
        pipeline_id: selectedPipeline,
        trigger_stage_id: selectedStage,
        project_status_id: Number(selectedStatusId),
        company_id: selectedCompanyId ? Number(selectedCompanyId) : null,
        is_active: isActive,
        sync_contact_notes: syncContactNotes,
        field_mappings: fieldMappings.map((m) => ({
          source_field_key: m.source_field_key,
          target_field: m.target_field,
        })),
      };

      try {
        setSaving(true);

        // 1. Save/create the primary config
        if (editingConfig) {
          await updateProjectStageConfig(editingConfig.id, payload, ssoToken);
        } else {
          await createProjectStageConfig(payload, ssoToken);
        }

        // 2. Save stage mappings (create/update additional configs)
        const currentConfigIds = stageMappings
          .map((m) => m.configId)
          .filter((id): id is number => id !== undefined);

        for (const mapping of stageMappings) {
          const stageMappingPayload = {
            pipeline_id: selectedPipeline,
            trigger_stage_id: mapping.stageId,
            project_status_id: Number(mapping.statusId),
            company_id: selectedCompanyId ? Number(selectedCompanyId) : null,
            is_active: isActive,
            sync_contact_notes: syncContactNotes,
            field_mappings: [] as { source_field_key: string; target_field: string }[],
          };

          if (mapping.configId) {
            await updateProjectStageConfig(mapping.configId, stageMappingPayload, ssoToken); // eslint-disable-line no-await-in-loop
          } else {
            await createProjectStageConfig(stageMappingPayload, ssoToken); // eslint-disable-line no-await-in-loop
          }
        }

        // 3. Delete removed mappings
        const removedIds = originalAdditionalConfigIds.current.filter(
          (id) => !currentConfigIds.includes(id)
        );
        for (const id of removedIds) {
          await deleteProjectStageConfig(id, ssoToken); // eslint-disable-line no-await-in-loop
        }

        onSave();
      } catch (err) {
        setError(parseApiError(err));
        // eslint-disable-next-line no-console
        console.error('Error saving job config:', err);
      } finally {
        setSaving(false);
      }
    },
    [selectedPipeline, selectedStage, selectedStatusId, selectedCompanyId, isActive, syncContactNotes, fieldMappings, stageMappings, editingConfig, ssoToken, onSave]
  );

  const handleDelete = useCallback(async () => {
    if (!editingConfig) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Delete configuration for this pipeline? This will remove all stage mappings and cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError(null);

      // Delete all configs for this pipeline (primary + additional)
      for (const config of pipelineConfigs) {
        await deleteProjectStageConfig(config.id, ssoToken); // eslint-disable-line no-await-in-loop
      }

      onSave();
    } catch (err) {
      setError(parseApiError(err));
      // eslint-disable-next-line no-console
      console.error('Error deleting config:', err);
    } finally {
      setDeleting(false);
    }
  }, [editingConfig, pipelineConfigs, ssoToken, onSave]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2 text-muted">Loading configuration options...</span>
      </div>
    );
  }

  const submitLabel = editingConfig ? 'Update Configuration' : 'Create Configuration';
  const isLocked = !!lockedPipeline;

  return (
    <div className={classes.formContainer}>
      <div className={classes.formHeader}>
        <button type="button" className="btn btn-link text-decoration-none p-0 me-2" onClick={onCancel}>
          &larr; Back
        </button>
        <h5 className="mb-0">
          {lockedPipeline ? lockedPipeline.name : (editingConfig ? 'Edit Configuration' : 'New Configuration')}
        </h5>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="jc-pipeline" className="form-label">
              {'Pipeline '}
              <span className="text-danger">*</span>
            </label>
            <select
              id="jc-pipeline"
              className="form-select"
              value={selectedPipeline}
              onChange={(e) => handlePipelineChange(e.target.value)}
              disabled={isLocked}
              required
            >
              <option value="">Select a pipeline...</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="jc-stage" className="form-label">
              {'Trigger Stage '}
              <span className="text-danger">*</span>
            </label>
            <select
              id="jc-stage"
              className="form-select"
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              disabled={!selectedPipeline}
              required
            >
              <option value="">
                {selectedPipeline ? 'Select a stage...' : 'Select a pipeline first'}
              </option>
              {selectedPipeline && (
                <option value="*">Any Stage (create project regardless of stage)</option>
              )}
              {stages.map((stage) => {
                const usedInMapping = stageMappings.some((m) => m.stageId === stage.id);
                return (
                  <option
                    key={stage.id}
                    value={stage.id}
                    disabled={usedInMapping && selectedStage !== stage.id}
                  >
                    {stage.name}
                  </option>
                );
              })}
            </select>
            <div className="form-text">
              {selectedStage === '*'
                ? 'A RocketPlan project will be created when an opportunity is added to any stage in this pipeline.'
                : 'When opportunities reach this stage, a RocketPlan project will be created.'}
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="jc-status" className="form-label">
              {'Project Status '}
              <span className="text-danger">*</span>
            </label>
            <select
              id="jc-status"
              className="form-select"
              value={selectedStatusId}
              onChange={(e) => setSelectedStatusId(e.target.value)}
              required
            >
              <option value="">Select a status...</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <div className="form-text">
              The initial status assigned to the created project.
            </div>
          </div>

          <div className="col-md-6">
            <label htmlFor="jc-company" className="form-label">
              Company
            </label>
            <select
              id="jc-company"
              className="form-select"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            <div className="form-text">
              Override which RocketPlan company projects are created in.
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 d-flex align-items-center">
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="jc-active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="jc-active">
                Active
              </label>
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-center">
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="jc-sync-notes"
                checked={syncContactNotes}
                onChange={(e) => setSyncContactNotes(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="jc-sync-notes">
                Sync Contact Notes
              </label>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <FieldMappingEditor
            mappings={fieldMappings}
            sourceFields={sourceFields}
            onAdd={handleAddMapping}
            onChange={setFieldMappings}
          />
        </div>

        <div className="mb-3">
          <StageMappingEditor
            mappings={stageMappings}
            stages={stages}
            statuses={statuses}
            excludeStageIds={selectedStage ? [selectedStage] : []}
            onAdd={handleAddStageMapping}
            onChange={setStageMappings}
          />
        </div>

        <div className={classes.buttonGroup}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || deleting || !selectedPipeline || !selectedStage || !selectedStatusId}
          >
            {saving
              ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              )
              : submitLabel}
          </button>
          {editingConfig && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleDelete}
              disabled={saving || deleting}
            >
              {deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

const JobConfigFormMemo = memo(JobConfigForm, areEqual);

export { JobConfigFormMemo as JobConfigForm };
